import { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import { Camera, ChevronLeft, ChevronRight, X, Check, Search } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { SignedImage } from '../common/SignedFile';

interface TrailerPhotoWizardProps {
  existingData?: any | null;
  onClose: () => void;
  onSuccess: () => void;
}

interface PhotoCategory {
  id: string;
  label: string;
  description: string;
  fieldName: keyof PhotoData;
}

interface PhotoData {
  pictures_4_sides: File[];
  tires: File[];
  visible_damage: File[];
}

export function TrailerPhotoWizard({ existingData, onClose, onSuccess }: TrailerPhotoWizardProps) {
  const { user, profile } = useAuth();
  const { showToast } = useToast();
  const [currentStep, setCurrentStep] = useState(1);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Form data - initialize with existing data if editing
  const [inspectionId] = useState(existingData?.id || null);
  const [trailerNumber, setTrailerNumber] = useState(existingData?.trailer_number || '');
  const [trailerSearchQuery, setTrailerSearchQuery] = useState(existingData?.trailer_number || '');
  const [trailers, setTrailers] = useState<any[]>([]);
  const [showTrailerDropdown, setShowTrailerDropdown] = useState(false);
  const [outIn, setOutIn] = useState<'OUT' | 'IN'>(existingData?.out_in || 'OUT');
  const [driverName, setDriverName] = useState(existingData?.driver_name || '');
  const [driverSearchQuery, setDriverSearchQuery] = useState(existingData?.driver_name || '');
  const [drivers, setDrivers] = useState<any[]>([]);
  const [showDriverDropdown, setShowDriverDropdown] = useState(false);
  const [company, setCompany] = useState(existingData?.company || '');
  const [companies, setCompanies] = useState<any[]>([]);
  const [inspectionDate, setInspectionDate] = useState(existingData?.inspection_date || new Date().toISOString().split('T')[0]);
  const [yardLetterPhotos, setYardLetterPhotos] = useState<File[]>([]);
  const [existingYardLetterUrl] = useState(existingData?.yard_letter || null);
  const [currentPhotoCategory, setCurrentPhotoCategory] = useState(0);
  const yardLetterInputRef = useRef<HTMLInputElement>(null);

  // Photo data - only 3 categories for trailers
  const [photoData, setPhotoData] = useState<PhotoData>({
    pictures_4_sides: [],
    tires: [],
    visible_damage: [],
  });

  const photoCategories: PhotoCategory[] = [
    { id: '4-sides', label: 'Pictures of All 4 Sides', description: 'Take photos of front, back, left side, and right side of the trailer', fieldName: 'pictures_4_sides' },
    { id: 'tires', label: 'Tires', description: 'Take photos of all tires showing tread depth and condition', fieldName: 'tires' },
    { id: 'damage', label: 'Visible Damage', description: 'Take photos of any visible damage or issues (if none, skip)', fieldName: 'visible_damage' },
  ];

  const personName = profile?.full_name || user?.email?.split('@')[0] || 'User';

  // Load trailers and drivers for search
  useEffect(() => {
    loadTrailers();
    loadDrivers();
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('id, name, code')
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setCompanies(data || []);
    } catch (error: any) {
      console.error('Error loading companies:', error);
    }
  };

  const loadTrailers = async () => {
    try {
      const { data, error } = await supabase
        .from('trailers')
        .select('id, unit_number, make, model, status')
        .order('unit_number');

      if (error) throw error;
      setTrailers(data || []);
    } catch (error) {
      console.error('Error loading trailers:', error);
    }
  };

  const loadDrivers = async () => {
    try {
      const { data, error } = await supabase
        .from('drivers')
        .select('id, first_name, last_name, phone_number, companies(code)')
        .order('first_name');

      if (error) throw error;
      setDrivers(data || []);
    } catch (error) {
      console.error('Error loading drivers:', error);
    }
  };

  const filteredTrailers = trailers.filter(trailer =>
    trailer.unit_number.toLowerCase().includes(trailerSearchQuery.toLowerCase())
  );

  const filteredDrivers = drivers.filter(driver =>
    `${driver.first_name} ${driver.last_name}`.toLowerCase().includes(driverSearchQuery.toLowerCase())
  );

  const handleTrailerSelect = (trailer: any) => {
    setTrailerNumber(trailer.unit_number);
    setTrailerSearchQuery(trailer.unit_number);
    setShowTrailerDropdown(false);
  };

  const handleDriverSelect = (driver: any) => {
    const fullName = `${driver.first_name} ${driver.last_name}`;
    setDriverName(fullName);
    setDriverSearchQuery(fullName);
    setShowDriverDropdown(false);
    
    // Auto-populate company if available
    if (driver.companies?.code) {
      setCompany(driver.companies.code);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      const category = photoCategories[currentPhotoCategory];
      setPhotoData(prev => ({
        ...prev,
        [category.fieldName]: [...prev[category.fieldName], ...files]
      }));
    }
  };

  const handleCameraCapture = () => {
    fileInputRef.current?.click();
  };

  const removePhoto = (fieldName: keyof PhotoData, index: number) => {
    setPhotoData(prev => ({
      ...prev,
      [fieldName]: prev[fieldName].filter((_, i) => i !== index)
    }));
  };

  const handleYardLetterCapture = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 0) {
      setYardLetterPhotos(prev => [...prev, ...files]);
    }
  };

  const convertImageToJPEG = async (file: File): Promise<File> => {
    // Check if conversion is needed
    const needsConversion = ['image/avif', 'image/heic', 'image/heif'].includes(file.type.toLowerCase());
    
    if (!needsConversion) {
      return file;
    }

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          ctx?.drawImage(img, 0, 0);
          
          canvas.toBlob((blob) => {
            if (blob) {
              const newFile = new File([blob], file.name.replace(/\.[^/.]+$/, '.jpg'), {
                type: 'image/jpeg',
              });
              resolve(newFile);
            } else {
              reject(new Error('Failed to convert image'));
            }
          }, 'image/jpeg', 0.9);
        };
        img.onerror = reject;
        img.src = e.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const uploadPhotosToStorage = async (files: File[], category: string): Promise<string[]> => {
    const uploadedUrls: string[] = [];

    for (const file of files) {
      try {
        // Convert to JPEG if needed
        const processedFile = await convertImageToJPEG(file);
        
        const fileName = `${trailerNumber}-${category}-${Date.now()}-${Math.random().toString(36).substring(7)}.jpg`;
        const filePath = `trailer-photos/${fileName}`;

        const { error: uploadError } = await supabase.storage
          .from('logos')
          .upload(filePath, processedFile);

        if (uploadError) throw uploadError;

        const { data: { publicUrl } } = supabase.storage
          .from('logos')
          .getPublicUrl(filePath);

        uploadedUrls.push(publicUrl);
      } catch (error) {
        console.error('Error uploading photo:', error);
        throw error;
      }
    }

    return uploadedUrls;
  };

  const handleSubmit = async () => {
    try {
      setUploading(true);

      // Upload all photos to storage
      const uploadedData: any = {};
      
      for (const category of photoCategories) {
        const files = photoData[category.fieldName];
        if (files.length > 0) {
          // Upload new photos
          const newUrls = await uploadPhotosToStorage(files, category.id);
          // Merge with existing photos if editing
          const existingUrls = existingData?.[category.fieldName] || [];
          uploadedData[category.fieldName] = [...existingUrls, ...newUrls];
        } else if (existingData?.[category.fieldName]) {
          // Keep existing photos if no new ones uploaded
          uploadedData[category.fieldName] = existingData[category.fieldName];
        } else {
          uploadedData[category.fieldName] = [];
        }
      }

      // Upload yard letter photos
      let yardLetterUrl = existingYardLetterUrl;
      if (yardLetterPhotos.length > 0) {
        const yardLetterUrls = await uploadPhotosToStorage(yardLetterPhotos, 'yard-letter');
        yardLetterUrl = yardLetterUrls[0];
      }

      const dataToSave = {
        trailer_number: trailerNumber,
        person: personName,
        out_in: outIn,
        driver_name: driverName || null,
        company: company || null,
        inspection_date: inspectionDate,
        yard_letter: yardLetterUrl || null,
        ...uploadedData,
        updated_by: user?.email,
      };

      let error;
      if (inspectionId) {
        // Update existing record
        const result = await supabase
          .from('trailer_photos')
          .update(dataToSave)
          .eq('id', inspectionId);
        error = result.error;
      } else {
        // Insert new record
        const result = await supabase
          .from('trailer_photos')
          .insert({
            ...dataToSave,
            created_by: user?.email,
          });
        error = result.error;
      }

      if (error) throw error;

      showToast('success', inspectionId ? 'Trailer inspection updated successfully!' : 'Trailer photos submitted successfully!');
      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Error submitting trailer photos:', error);
      showToast('error', error.message || 'Failed to submit trailer photos');
    } finally {
      setUploading(false);
    }
  };

  const canProceedToNextStep = () => {
    switch (currentStep) {
      case 1:
        return trailerNumber.trim() !== '';
      case 2:
        return driverName.trim() !== '' && company.trim() !== '';
      case 3:
        return true; // Can always proceed from photo step
      case 4:
        return true; // Review step
      default:
        return false;
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Trailer Information</h2>
              <p className="text-slate-600">Enter the trailer number and inspection status</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Trailer Number *
              </label>
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={trailerSearchQuery}
                    onChange={(e) => {
                      setTrailerSearchQuery(e.target.value);
                      setShowTrailerDropdown(true);
                    }}
                    onFocus={() => setShowTrailerDropdown(true)}
                    placeholder="Search trailer number..."
                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                  />
                </div>
                
                {showTrailerDropdown && filteredTrailers.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredTrailers.map((trailer) => (
                      <button
                        key={trailer.id}
                        onClick={() => handleTrailerSelect(trailer)}
                        className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b border-slate-100 last:border-b-0"
                      >
                        <div className="font-medium text-slate-900">{trailer.unit_number}</div>
                        <div className="text-sm text-slate-600">{trailer.make} {trailer.model}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Inspector
              </label>
              <input
                type="text"
                value={personName}
                disabled
                className="w-full px-4 py-3 border border-slate-300 rounded-lg bg-slate-50 text-slate-600 text-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Status *
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setOutIn('OUT')}
                  className={`py-4 rounded-lg font-semibold text-lg transition-all ${
                    outIn === 'OUT'
                      ? 'bg-orange-600 text-white shadow-lg'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  OUT
                </button>
                <button
                  onClick={() => setOutIn('IN')}
                  className={`py-4 rounded-lg font-semibold text-lg transition-all ${
                    outIn === 'IN'
                      ? 'bg-green-600 text-white shadow-lg'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                >
                  IN
                </button>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Additional Information</h2>
              <p className="text-slate-600">Driver and company details</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Driver Name *
              </label>
              <div className="relative">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text"
                    value={driverSearchQuery}
                    onChange={(e) => {
                      setDriverSearchQuery(e.target.value);
                      setShowDriverDropdown(true);
                    }}
                    onFocus={() => setShowDriverDropdown(true)}
                    placeholder="Search driver name..."
                    className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                  />
                </div>
                
                {showDriverDropdown && filteredDrivers.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredDrivers.map((driver) => (
                      <button
                        key={driver.id}
                        onClick={() => handleDriverSelect(driver)}
                        className="w-full px-4 py-3 text-left hover:bg-blue-50 border-b border-slate-100 last:border-b-0"
                      >
                        <div className="font-medium text-slate-900">
                          {driver.first_name} {driver.last_name}
                        </div>
                        <div className="text-sm text-slate-600">{driver.phone_number}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">
                Company *
              </label>
              <div className="grid grid-cols-1 gap-3">
                {companies.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setCompany(c.code)}
                    className={`py-4 px-6 rounded-lg font-semibold text-lg transition-all ${
                      company === c.code
                        ? 'bg-blue-600 text-white shadow-lg'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {c.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Inspection Date
              </label>
              <input
                type="date"
                value={inspectionDate}
                onChange={(e) => setInspectionDate(e.target.value)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Yard Letter (Photo)
              </label>
              
              {/* Photo Preview */}
              {(yardLetterPhotos.length > 0 || existingYardLetterUrl) && (
                <div className="mb-3 grid grid-cols-2 gap-3">
                  {existingYardLetterUrl && yardLetterPhotos.length === 0 && (
                    <div className="relative aspect-square rounded-lg overflow-hidden bg-slate-100">
                      <SignedImage
                        stored={existingYardLetterUrl}
                        alt="Existing Yard Letter"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 left-2 px-2 py-1 bg-blue-600 text-white text-xs rounded">
                        Existing
                      </div>
                    </div>
                  )}
                  {yardLetterPhotos.map((file, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100">
                      <img
                        src={URL.createObjectURL(file)}
                        alt={`Yard Letter ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => setYardLetterPhotos(prev => prev.filter((_, i) => i !== index))}
                        className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <input
                ref={yardLetterInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                multiple
                onChange={handleYardLetterCapture}
                className="hidden"
              />
              
              <button
                onClick={() => yardLetterInputRef.current?.click()}
                className="w-full py-4 bg-slate-600 text-white rounded-lg font-semibold text-lg flex items-center justify-center gap-3 hover:bg-slate-700 active:bg-slate-800 transition-colors"
              >
                <Camera className="w-6 h-6" />
                {yardLetterPhotos.length > 0 ? 'Add More Photos' : 'Take Photo of Yard Letter'}
              </button>
              
              {yardLetterPhotos.length > 0 && (
                <p className="text-center text-sm text-slate-500 mt-2">
                  {yardLetterPhotos.length} photo{yardLetterPhotos.length !== 1 ? 's' : ''} added
                </p>
              )}
            </div>
          </div>
        );

      case 3:
        const category = photoCategories[currentPhotoCategory];
        const categoryPhotos = photoData[category.fieldName];

        return (
          <div className="space-y-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <h2 className="text-xl font-bold text-slate-900">{category.label}</h2>
                <span className="text-sm text-slate-600">
                  {currentPhotoCategory + 1} of {photoCategories.length}
                </span>
              </div>
              <p className="text-slate-600">{category.description}</p>
            </div>

            {(categoryPhotos.length > 0 || (existingData?.[category.fieldName] && existingData[category.fieldName].length > 0)) && (
              <div className="grid grid-cols-2 gap-3">
                {/* Show existing photos */}
                {existingData?.[category.fieldName]?.map((url: string, index: number) => (
                  <div key={`existing-${index}`} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100">
                    <SignedImage
                      stored={url}
                      alt={`Existing ${category.label} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 left-2 px-2 py-1 bg-blue-600 text-white text-xs rounded">
                      Existing
                    </div>
                  </div>
                ))}
                {/* Show new photos */}
                {categoryPhotos.map((file, index) => (
                  <div key={`new-${index}`} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100">
                    <img
                      src={URL.createObjectURL(file)}
                      alt={`${category.label} ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                    <button
                      onClick={() => removePhoto(category.fieldName, index)}
                      className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full hover:bg-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              multiple
              onChange={handleFileSelect}
              className="hidden"
            />

            <button
              onClick={handleCameraCapture}
              className="w-full py-6 bg-blue-600 text-white rounded-lg font-semibold text-lg flex items-center justify-center gap-3 hover:bg-blue-700 active:bg-blue-800 transition-colors"
            >
              <Camera className="w-6 h-6" />
              Take Photo{categoryPhotos.length > 0 ? 's' : ''}
            </button>

            <div className="flex gap-3">
              {currentPhotoCategory > 0 && (
                <button
                  onClick={() => setCurrentPhotoCategory(prev => prev - 1)}
                  className="flex-1 py-3 bg-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-300"
                >
                  Previous Category
                </button>
              )}
              <button
                onClick={() => {
                  if (currentPhotoCategory < photoCategories.length - 1) {
                    setCurrentPhotoCategory(prev => prev + 1);
                  } else {
                    setCurrentStep(4);
                  }
                }}
                className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
              >
                {currentPhotoCategory < photoCategories.length - 1 ? 'Next Category' : 'Review'}
              </button>
            </div>
          </div>
        );

      case 4:
        const totalPhotos = Object.values(photoData).reduce((sum, photos) => sum + photos.length, 0) + yardLetterPhotos.length;
        const existingPhotosCount = existingData ? 
          Object.keys(photoData).reduce((sum, key) => {
            const existing = existingData[key as keyof PhotoData];
            return sum + (Array.isArray(existing) ? existing.length : 0);
          }, 0) + (existingYardLetterUrl ? 1 : 0) : 0;

        return (
          <div className="space-y-6">
            <div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Review & Submit</h2>
              <p className="text-slate-600">Please review all information before submitting</p>
            </div>

            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between">
                <span className="text-slate-600">Trailer Number:</span>
                <span className="font-medium text-slate-900">{trailerNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Inspector:</span>
                <span className="font-medium text-slate-900">{personName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Status:</span>
                <span className={`font-medium ${outIn === 'OUT' ? 'text-orange-600' : 'text-green-600'}`}>
                  {outIn}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Driver:</span>
                <span className="font-medium text-slate-900">{driverName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Company:</span>
                <span className="font-medium text-slate-900">{company}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Date:</span>
                <span className="font-medium text-slate-900">
                  {new Date(inspectionDate).toLocaleDateString()}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-slate-600">Yard Letter:</span>
                <span className="font-medium text-slate-900">
                  {yardLetterPhotos.length > 0 || existingYardLetterUrl ? '✓ Added' : 'Not added'}
                </span>
              </div>
              <div className="flex justify-between pt-3 border-t border-slate-200">
                <span className="text-slate-600">Total Photos:</span>
                <span className="font-bold text-slate-900">
                  {totalPhotos + existingPhotosCount}
                  {existingPhotosCount > 0 && ` (${existingPhotosCount} existing + ${totalPhotos} new)`}
                </span>
              </div>
            </div>

            <button
              onClick={handleSubmit}
              disabled={uploading}
              className="w-full py-4 bg-green-600 text-white rounded-lg font-semibold text-lg flex items-center justify-center gap-3 hover:bg-green-700 disabled:bg-slate-400 disabled:cursor-not-allowed transition-colors"
            >
              {uploading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Check className="w-6 h-6" />
                  {inspectionId ? 'Update Inspection' : 'Submit Inspection'}
                </>
              )}
            </button>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-slate-900">
              {inspectionId ? 'Edit Trailer Inspection' : 'Trailer Inspection Wizard'}
            </h1>
            <p className="text-sm text-slate-600">Step {currentStep} of 4</p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-slate-600" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <div className="flex gap-2">
            {[1, 2, 3, 4].map((step) => (
              <div
                key={step}
                className={`h-2 flex-1 rounded-full transition-all ${
                  step <= currentStep ? 'bg-blue-600' : 'bg-slate-200'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {renderStepContent()}
        </div>

        {/* Footer */}
        {currentStep < 4 && (
          <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex gap-3">
            {currentStep > 1 && currentStep !== 3 && (
              <button
                onClick={() => setCurrentStep(prev => prev - 1)}
                className="flex-1 py-3 bg-slate-200 text-slate-700 rounded-lg font-medium hover:bg-slate-300"
              >
                <ChevronLeft className="w-5 h-5 inline mr-1" />
                Back
              </button>
            )}
            <button
              onClick={() => setCurrentStep(prev => prev + 1)}
              disabled={!canProceedToNextStep()}
              className="flex-1 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight className="w-5 h-5 inline ml-1" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
